import { Box, TextFieldProps } from '@mui/material';
import type { ChangeEvent, FC } from 'react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import type { NumberFormatProps, NumberFormatValues } from 'react-number-format';
import NumberFormat from 'react-number-format';
import type { BankLogo } from './constants';
import { formatCreditCard, getBankIconFromCardNumber, persianNumsToEnglish } from './utils';
import { TextField } from '@/ui/kit/components/textfield';
interface BankCardFieldProps extends NumberFormatProps {
  maskCharacter?: string;
  separatorCharacter?: string;
}
const CustomTextField = (props: TextFieldProps) => {
  return <TextField {...props} onChange={(e: ChangeEvent<HTMLInputElement>) => {
    e.target.value = persianNumsToEnglish(e.target.value);
    props.onChange?.(e);
  }} data-sentry-element="TextField" data-sentry-component="CustomTextField" data-sentry-source-file="BankCardField.tsx" />;
};
export const BankCardField: FC<BankCardFieldProps> = forwardRef<HTMLInputElement, BankCardFieldProps>(function BankCardField({
  InputProps: InputPropsProp,
  onValueChange,
  maskCharacter = '',
  separatorCharacter,
  label = 'شماره کارت',
  ...rest
}) {
  const [bankLogo, setBankLogo] = useState<BankLogo>();
  const InputProps = useMemo(() => ({
    sx: {
      direction: 'rtl',
      textAlign: 'center'
    },
    endAdornment: <Box sx={{
      ml: 1.5,
      display: 'flex'
    }}>{bankLogo?.icon}</Box>,
    ...InputPropsProp
  }), [bankLogo, InputPropsProp]);
  const valueChangeHandler = useCallback((data: NumberFormatValues) => {
    onValueChange?.(data);
  }, [onValueChange]);
  const customFormat = useCallback((data: string): string => {
    let val = data.replace(/\D/g, '').slice(0, 16);
    if (val.length > 16) {
      val = data.substring(0, 16);
    }
    setBankLogo(getBankIconFromCardNumber(val));
    return formatCreditCard(val, maskCharacter, separatorCharacter);
  }, [maskCharacter, separatorCharacter]);
  const removedNoneNumberChars = useCallback((formattedValue: string): string => {
    return formattedValue?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
  }, []);
  return <NumberFormat allowEmptyFormatting required customInput={CustomTextField} data-testid='bank-card-field' format={customFormat} InputProps={InputProps} isNumericString={true} label={label} removeFormatting={removedNoneNumberChars} size='large' type='tel' onValueChange={valueChangeHandler} {...rest} />;
});